import React, { createContext, useContext, useEffect, useState } from 'react';
import io, { Socket } from 'socket.io-client';
import { useAuth } from './AuthContext';

interface WebSocketContextType {
  socket: Socket | null;
  isConnected: boolean;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    // Stratégie d'URL Socket.IO :
    //   - En développement : on utilise window.location.origin pour que le
    //     proxy CRA (setupProxy.js) intercepte /socket.io/* et route vers
    //     PROXY_SOCKET_TARGET. Ça marche quel que soit le port effectif du
    //     frontend (3000, 3001 si 3000 est pris, etc.). Avant ce fix,
    //     REACT_APP_WEBSOCKET_URL était hardcodé à http://localhost:3000
    //     dans .env.local et provoquait un CORS bloqué quand le frontend
    //     tournait sur :3001.
    //   - En production : on utilise REACT_APP_WEBSOCKET_URL (typiquement
    //     https://socket.macadam-ia.com) pour pointer directement sur le
    //     serveur Socket.IO de prod.
    const socketUrl =
      process.env.NODE_ENV === 'development'
        ? window.location.origin
        : process.env.REACT_APP_WEBSOCKET_URL || 'http://localhost:4000';

    const newSocket = io(socketUrl, {
      withCredentials: true, // Le cookie httpOnly est envoyé automatiquement
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });

    newSocket.on('connect', () => {
      setIsConnected(true);
    });

    newSocket.on('disconnect', (reason) => {
      setIsConnected(false);
    });

    newSocket.on('connect_error', (error) => {
      console.error('[WebSocket] Erreur de connexion', {
        timestamp: new Date().toISOString(),
        message: error.message,
        description: (error as any).description, // Socket.io peut ajouter cette propriété
      });
      setIsConnected(false);
    });

    newSocket.on('error', (error) => {
      console.error('[WebSocket] Erreur', {
        timestamp: new Date().toISOString(),
        error,
      });
    });

    newSocket.io.on('reconnect_attempt', (attempt) => {
    });

    newSocket.io.on('reconnect', (attempt) => {
    });

    newSocket.io.on('reconnect_failed', () => {
      console.error('[WebSocket] Échec de reconnexion - toutes les tentatives épuisées');
    });

    setSocket(newSocket);

    return () => {
      newSocket.removeAllListeners();
      newSocket.disconnect();
    };
  }, [isAuthenticated]);

  return (
    <WebSocketContext.Provider value={{ socket, isConnected }}>
      {children}
    </WebSocketContext.Provider>
  );
};
